import * as _ from 'lodash';
import { DateTime } from 'luxon';
import React, { useState } from 'react';
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Line,
  Cell,
  Area,
} from 'recharts';

import 'source/packages/styles/common.scss';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  ChartTimePeriod,
  dateFormatter,
  findChartDataForReportingDate,
  PortfolioChartConfig,
  PortfolioChartFormat,
  tooltipFormatter,
  valueTypeToFormatterMap,
} from './portfolioChartUtils';
import { Props as DefaultLegendContentProps } from 'recharts/types/component/DefaultLegendContent';
import { AxisDomain } from 'recharts/types/util/types';
import { colors } from '~/packages/common-frontend/colors';

const tooltipLabelFormatterDate = (value: string) => {
  return DateTime.fromISO(value).toFormat('LLLL dd, yyyy');
};

const tooltipLabelFormatterEpoch = (value: number) => {
  return commonDate.epochToLocaleDateString(
    value,
    undefined,
    DateTime.DATE_FULL,
  );
};

const tooltipLabelFormatterMonthYear = (date: Util.FullDateStr) => {
  return `${commonDate.fullDateStrToStr(
    date,
    'LLLL',
  )} ${commonDate.fullDateStrToStr(date, 'yyyy')}`;
};

const tooltipLabelFormatter: {
  [key in 'date' | 'epoch' | 'monthYear']: (value: any) => string;
} = {
  date: tooltipLabelFormatterDate,
  epoch: tooltipLabelFormatterEpoch,
  monthYear: tooltipLabelFormatterMonthYear,
};

type ChartData = {
  reportingDate: Util.FullDateStr;
  [key: string]: Util.Primitive;
};
type PortfolioReChartProps<T extends ChartData> = {
  chartData: T[];
  selectedTimePeriod: ChartTimePeriod;
  currentSelectedBarReportingDate: Util.FullDateStr;
  onSelectBar: (selectedBarData: T) => void;
  chartConfig: PortfolioChartConfig<any>;
  visibleChartComponents: (keyof T)[];
  renderCustomLegend?: (props: DefaultLegendContentProps) => React.ReactElement;
  leftYAxisDomain?: AxisDomain;
  tooltipLabelType?: keyof typeof tooltipLabelFormatter;
  versionFieldName?: string;
  chartMargin?: { top: number; right: number; left: number; bottom: number };
  legendAlign?: {
    horizontalAlign: 'left' | 'center' | 'right';
    verticalAlign: 'top' | 'middle' | 'bottom';
  };
  legendWrapperStyle?: React.CSSProperties;
  customTooltipFormatter?: (value: number, name: string) => [string, string];
};
export function PortfolioReChart<T extends ChartData>({
  chartData,
  currentSelectedBarReportingDate,
  onSelectBar,
  selectedTimePeriod,
  chartConfig,
  visibleChartComponents,
  renderCustomLegend,
  leftYAxisDomain,
  tooltipLabelType,
  versionFieldName = 'fileVersion',
  chartMargin,
  customTooltipFormatter,
  legendAlign = { horizontalAlign: 'left', verticalAlign: 'bottom' },
  legendWrapperStyle = { padding: '16px 24px' },
}: PortfolioReChartProps<T>) {
  const [mouseHoverBarIndex, setMouseHoverBarIndex] = useState<number | null>(
    null,
  );

  // do not consider 'boolean' as a chart format to display as an axis
  const chartConfigFormatsForAxes = _.chain(chartConfig)
    .map((config) => config && config.format)
    .filter((format) => format !== 'boolean')
    .compact()
    .uniq()
    .value();

  const configFormatMap = new Map<string, PortfolioChartFormat>();
  Object.values(chartConfig).forEach((config) => {
    if (config) {
      configFormatMap.set(config.field, config.format);
    }
  });

  const tooltipFormatterFn =
    customTooltipFormatter ??
    ((value: number, name: string) => {
      const format = configFormatMap.get(name);
      if (!format) {
        return [
          typeof value === 'number' ? value.toFixed(2) : value,
          commonUtils.toTitleCase(name),
        ];
      }
      return tooltipFormatter(value, name, format);
    });

  return (
    <ResponsiveContainer width="100%" height={500}>
      <ComposedChart
        data={chartData}
        margin={chartMargin || { top: 20, right: 30, left: 30, bottom: 0 }}
      >
        <CartesianGrid vertical={false} />
        {/* for area chart gradient */}
        <defs>
          {_.compact(
            _.map(chartConfig, (config, fieldName) => {
              if (config?.chartType === 'area') {
                return (
                  <linearGradient
                    id={fieldName}
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="0.7"
                    key={fieldName}
                  >
                    <stop stopColor={config.color} />
                    <stop offset="1" stopColor="white" stopOpacity="0.15" />
                  </linearGradient>
                );
              }
              return undefined;
            }),
          )}
        </defs>
        <XAxis
          dataKey="reportingDate"
          height={70}
          tick={{ fontWeight: 600, dy: '24px' }}
          tickFormatter={(value: any) =>
            dateFormatter({ date: value, timePeriod: selectedTimePeriod })
          }
          axisLine={false}
          tickLine={false}
          tickMargin={24}
          cursor="pointer"
          onClick={(clickedReportingDateProps: any) => {
            // must type onClick prop as 'any' because the library's type for this is a React MouseEvent which does not carry the 'value' key
            // but this event prop in reality does carry the 'value' key
            const chartDataForReportingDate = findChartDataForReportingDate(
              chartData,
              clickedReportingDateProps.value,
              versionFieldName,
            );
            if (chartDataForReportingDate) {
              onSelectBar(chartDataForReportingDate);
            }
          }}
        />

        <YAxis
          tickFormatter={valueTypeToFormatterMap[chartConfigFormatsForAxes[0]]}
          tick={{ fontWeight: 600 }}
          axisLine={false}
          tickLine={false}
          yAxisId={chartConfigFormatsForAxes[0]}
          orientation="left"
          domain={leftYAxisDomain}
          tickMargin={10}
        />
        {chartConfigFormatsForAxes[1] && (
          <YAxis
            tickFormatter={
              valueTypeToFormatterMap[chartConfigFormatsForAxes[1]]
            }
            tick={{ fontWeight: 600 }}
            axisLine={false}
            tickLine={false}
            yAxisId={chartConfigFormatsForAxes[1]}
            orientation="right"
          />
        )}

        <Tooltip
          formatter={tooltipFormatterFn}
          labelFormatter={
            tooltipLabelType
              ? tooltipLabelFormatter[tooltipLabelType]
              : undefined
          }
          itemStyle={{ color: colors.gray[500] }}
          cursor={{ stroke: 'none' }}
          contentStyle={{ fontSize: '12px' }}
        />
        <Legend
          formatter={(value) => {
            const chartConfigForField = chartConfig[value];
            const legendText = chartConfigForField
              ? chartConfigForField.label
              : _.startCase(value);
            return (
              <span
                style={{
                  color: 'black',
                  fontSize: '14px',
                  fontWeight: 500,
                  verticalAlign: 'text-top',
                  margin: '0 40px 0 16px',
                }}
              >
                {legendText}
              </span>
            );
          }}
          iconSize={20}
          align={legendAlign.horizontalAlign}
          verticalAlign={legendAlign.verticalAlign}
          wrapperStyle={legendWrapperStyle}
          content={renderCustomLegend}
        />
        {_.map(chartConfig, (config, fieldName) => {
          if (!config || !_.includes(visibleChartComponents, config.field)) {
            return <React.Fragment key={fieldName} />;
          }

          const {
            chartType,
            color,
            activeColor,
            stackId,
            format,
            conditionalStyle,
          } = config;
          const reactChildKey = `${chartType}-${fieldName}`;

          // This switch-case unfortunately has to be written in-line
          // and return direct Recharts components.
          // Wrapping the chart component as a reusable React component will not render.
          // https://github.com/recharts/recharts/issues/2788
          switch (chartType) {
            case 'bar':
              return (
                <Bar
                  key={reactChildKey}
                  cursor="pointer"
                  onClick={(data) => {
                    onSelectBar(data.payload);
                  }}
                  dataKey={fieldName}
                  barSize={64}
                  fill={color} // determines legend color, must keep even though cell color is individually managed
                  stackId={stackId}
                  yAxisId={format} // maps to the correct axis scale based on currency vs percentage
                >
                  {chartData.map((entry, index) => {
                    // bar is consider active if it is the selected bar or being hovered over
                    const isBarActive =
                      entry.reportingDate === currentSelectedBarReportingDate ||
                      index === mouseHoverBarIndex;

                    // if the config for this field has conditional styles
                    // and the condition passes for this particular bar
                    // then apply conditional style
                    const applyConditionalStyle =
                      conditionalStyle && conditionalStyle.condition(entry);
                    // add alert field here. field should be in chartData boolean
                    return (
                      <Cell
                        cursor="pointer"
                        fill={isBarActive ? activeColor ?? color : color}
                        key={`cell-${entry.reportingDate}=${entry.fileVersion}`}
                        onMouseEnter={() => setMouseHoverBarIndex(index)}
                        onMouseLeave={() => setMouseHoverBarIndex(null)}
                        style={
                          applyConditionalStyle
                            ? conditionalStyle.style
                            : undefined
                        }
                      />
                    );
                  })}
                </Bar>
              );
            case 'line':
              return (
                <Line
                  key={reactChildKey}
                  dataKey={fieldName}
                  fill={color}
                  stroke={color}
                  strokeWidth="2px"
                  dot={false}
                  yAxisId={format}
                />
              );
            case 'dashedLine':
              return (
                <Line
                  key={reactChildKey}
                  dataKey={fieldName}
                  fill={color}
                  stroke={color}
                  strokeWidth="1px"
                  strokeDasharray="3 3"
                  dot={false}
                  yAxisId={format}
                />
              );
            case 'area':
              return (
                <Area
                  key={reactChildKey}
                  type="monotone"
                  dataKey={fieldName}
                  stroke={color}
                  strokeWidth="1px"
                  fillOpacity={1}
                  fill={`url(#${fieldName})`} // the fill id refers to the linearGradient id defined above
                  yAxisId={format}
                />
              );
            case 'none':
              return null;
            default:
              return <></>;
          }
        })}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
